import React from 'react';

/**
 * Shared customer-facing vendor card.
 * Keep the card focused on identity, trust and the next action.
 */
const VendorCard = ({
  vendor,
  getImageUrl,
  onClick,
  onShare,
  compact = false,
  featured = false,
  actionLabel,
}) => {
  if (!vendor) return null;

  const isOpen = vendor.is_open !== false;
  const rating = Number(vendor.rating);
  const hasRating = Number.isFinite(rating) && rating > 0;
  const location = vendor.location || vendor.city || '';

  return (
    <article
      data-testid={`vendor-card-${vendor.id}`}
      className={`group bg-white border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-cyan-500 hover:shadow-md ${compact ? '' : 'h-full'}`}
    >
      <button
        type="button"
        onClick={() => onClick?.(vendor)}
        className="w-full text-left"
        aria-label={`View ${vendor.name} vendor`}
      >
        <div className="relative aspect-square bg-gray-50 overflow-hidden">
          <img
            src={getImageUrl ? getImageUrl(vendor.image_url) : vendor.image_url}
            alt={vendor.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
                    decoding="async"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://via.placeholder.com/300?text=Vendor';
            }}
          />
          {featured && (
            <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-1 rounded-full bg-yellow-400 text-yellow-900 shadow-sm">
              Featured
            </span>
          )}
          <span
            className={`absolute top-2 left-2 text-[10px] font-bold px-2 py-1 rounded-full backdrop-blur-sm ${
              isOpen ? 'bg-white/95 text-green-700' : 'bg-black/65 text-white'
            }`}
          >
            {isOpen ? 'Open' : 'Closed'}
          </span>
        </div>
      </button>

      <div className="p-2.5">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => onClick?.(vendor)}
            className="min-w-0 text-left"
          >
            <p className="text-sm font-bold text-gray-900 line-clamp-1">{vendor.name}</p>
            <p className="text-[11px] text-gray-500 line-clamp-1 mt-0.5">
              {vendor.vendor_type || 'Vendor'}
            </p>
          </button>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {actionLabel && (
              <span className="text-[10px] font-bold text-cyan-700 whitespace-nowrap">
                {actionLabel}
              </span>
            )}
            {onShare && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(vendor, e);
                }}
                className="w-8 h-8 rounded-full bg-gray-50 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700 flex items-center justify-center transition-colors"
                aria-label={`Share ${vendor.name}`}
                data-testid={`vendor-share-${vendor.id}`}
              >
                <i className="fa-solid fa-share-nodes text-xs" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2 text-[10px] text-gray-500 min-w-0">
          {hasRating && (
            <span className="inline-flex items-center gap-0.5 font-semibold text-gray-700">
              <i className="fa-solid fa-star text-yellow-500" />
              {rating.toFixed(1)}
            </span>
          )}
          {location && (
            <span className="inline-flex items-center gap-0.5 truncate">
              <i className="fa-solid fa-location-dot text-gray-400" />
              <span className="truncate">{location}</span>
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default VendorCard;
